// src/components/features/timetables/TimetablesTab/TimetablesTab.tsx
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { StopDeparturesDetailsPage } from '../../departures/StopDetailsPage/StopDeparturesDetailsPage';
import { LineTimetableDetailsPage } from '../DetailsPage/LineDetailsPage';
import { TimetableSearchPage } from './SearchPage';
import { TimetableResultsPage } from './ResultsPage';
import { useFindTimetable } from '../../../../data/useFindTimetable';
import {
  getTimetableLineId,
  getTimetableStopAtcoCode,
  getTimetableStopName,
  type TimetableStopDeparturesDetailsPageOptions,
  type JourneyWidgetPage,
} from '../../../widget/pageState';
import { useWidgetAnalytics } from '../../../../analytics/useWidgetAnalytics';
import styles from '../../../widget/Widget.module.css';

interface TimetablesTabProps {
  currentPage: JourneyWidgetPage;
  onShowResults: () => void;
  onShowDetails: (detailsId: string) => void;
  onShowStopDeparturesDetails?: (
    options: TimetableStopDeparturesDetailsPageOptions,
  ) => void;
}

/**
 * Top-level Timetable activity
 *
 * Includes:
 * - SearchPage with form
 * - ResultsPage with list of lines
 * - TimetableDetailsPage with Timetable and TimetableMap tabs
 */
export function TimetablesTab({
  currentPage,
  onShowResults,
  onShowDetails,
  onShowStopDeparturesDetails,
}: TimetablesTabProps) {
  const { track } = useWidgetAnalytics();
  const isSearchPage = currentPage.kind === 'search';
  const isResultsPage = currentPage.kind === 'results';
  const timetableResultsWasLoadingRef = useRef(false);

  const {
    query,
    submittedQuery,
    setQuery,
    handleSubmit,
    submitError,
    hasSubmitted,
    loading,
    error,
    lines,
  } = useFindTimetable();

  const selectedLineId = getTimetableLineId(currentPage);
  const selectedStopAtcoCode = getTimetableStopAtcoCode(currentPage);
  const selectedStopName = getTimetableStopName(currentPage);

  const selectedLine = useMemo(
    () => lines.find((line) => line.id === selectedLineId) ?? null,
    [lines, selectedLineId],
  );

  useEffect(() => {
    if (!hasSubmitted) return;
    if (loading) {
      timetableResultsWasLoadingRef.current = true;
      return;
    }
    if (!timetableResultsWasLoadingRef.current) return;
    timetableResultsWasLoadingRef.current = false;

    track({
      name: 'timetables_results_loaded',
      activity: 'timetables',
      page: 'results',
      payload: {
        status: error ? 'error' : lines.length === 0 ? 'empty' : 'success',
        resultCount: lines.length,
        lineIds: lines.map((l) => l.id),
        operatorCodes: [],
        operatorNames: lines
          .map((l) => l.operatorName)
          .filter((n): n is string => n !== null && n !== undefined),
        ...(error ? { errorMessage: error.message } : {}),
      },
    });
  }, [hasSubmitted, loading, lines, error, track]);

  const handleSearch = (event: Event) => {
    const didSubmit = handleSubmit(event as SubmitEvent);

    if (didSubmit) {
      track({
        name: 'timetables_search_submitted',
        activity: 'timetables',
        page: 'search',
        payload: { query },
      });
      onShowResults();
    }
  };

  const handleShowDetails = (detailsId: string) => {
    const line = lines.find((l) => l.id === detailsId);
    track({
      name: 'timetables_line_selected',
      activity: 'timetables',
      page: 'results',
      payload: {
        lineId: detailsId,
        lineName: line?.name ?? detailsId,
        operatorCode: null,
        operatorName: line?.operatorName ?? null,
      },
    });
    onShowDetails(detailsId);
  };

  return (
    <div className={styles.tabWrapper}>
      <div className={styles.tabPage}>
        {isSearchPage && (
          <TimetableSearchPage
            query={query}
            onQueryChange={setQuery}
            onSearch={handleSearch}
            submitError={submitError}
            loading={loading}
            hasSubmitted={hasSubmitted}
          />
        )}

        {isResultsPage && (
          <TimetableResultsPage
            draftQuery={query}
            submittedQuery={submittedQuery}
            onQueryChange={setQuery}
            onSearch={handleSearch}
            submitError={submitError}
            hasSubmitted={hasSubmitted}
            loading={loading}
            error={error}
            lines={lines}
            onShowDetails={handleShowDetails}
          />
        )}

        {currentPage.kind === 'details' &&
          (selectedStopAtcoCode ? (
            <StopDeparturesDetailsPage
              selectedStopAtcoCode={selectedStopAtcoCode}
              fallbackStopName={selectedStopName}
            />
          ) : (
            <LineTimetableDetailsPage
              selectedLineId={selectedLineId}
              initialLine={selectedLine}
              onShowStopDeparturesDetails={onShowStopDeparturesDetails}
            />
          ))}
      </div>
    </div>
  );
}
